/**
 * In-memory fake for CONTRACT 6 — `BindingResolver`.
 *
 * The fail-closed behaviour is the point: an unregistered address **throws
 * `TenantResolutionError`**, so a consumer's test proves it handles rejection rather than
 * discovering at runtime that there is no default VoiceAgent to fall back to (design 9.3).
 */
import { TenantResolutionError } from "../../error/taxonomy.js";
/** The `kind` a `TenantResolutionError` subject reports for each resolver method. */
const SUBJECT_KIND = Object.freeze({
    phone_number: "phone_number",
    sip_user: "sip_user",
    provider_webhook: "webhook_path",
    webrtc_path: "webrtc_path",
    web_chat_widget: "web_chat_widget",
    // A trunk number is a number; the taxonomy has no separate subject kind for it.
    sip_trunk_number: "phone_number",
});
export class FakeBindingResolver {
    /** `kind` + normalised address -> binding, mirroring the one global index of design 4.7. */
    #binding = new Map();
    /** Addresses registered as present-but-not-answering, so the ladder can be exercised. */
    #rejecting = new Map();
    /** Trunk numbers (plan-008), keyed like the bindings, carrying their trunk facts. */
    #sipTrunkNumber = new Map();
    #key(kind, address) {
        return `${kind}:${normalise(kind, address)}`;
    }
    register(kind, address, binding) {
        this.#binding.set(this.#key(kind, address), binding);
        return this;
    }
    /** Registers a trunk number (kind `sip_trunk_number`) with its trunk facts. */
    registerSipTrunkNumber(e164, resolved) {
        this.#sipTrunkNumber.set(this.#key("sip_trunk_number", e164), resolved);
        return this;
    }
    /** Registers an address that resolves to a binding which then fails a later rung. */
    registerRejecting(kind, address, stage) {
        this.#rejecting.set(this.#key(kind, address), stage);
        return this;
    }
    async ofNumber(e164) {
        return this.#resolve("phone_number", e164);
    }
    async ofSipUser(sipUri) {
        return this.#resolve("sip_user", sipUri);
    }
    async ofPath(kind, segment) {
        return this.#resolve(kind, segment);
    }
    async ofSipTrunkNumber(e164) {
        const key = this.#key("sip_trunk_number", e164);
        const stage = this.#rejecting.get(key);
        if (stage !== undefined)
            return Promise.reject(this.#error("sip_trunk_number", e164, stage));
        const found = this.#sipTrunkNumber.get(key);
        if (found === undefined)
            return Promise.reject(this.#error("sip_trunk_number", e164, "binding_not_found"));
        return Promise.resolve(found);
    }
    #resolve(kind, address) {
        const key = this.#key(kind, address);
        const stage = this.#rejecting.get(key);
        if (stage !== undefined)
            return Promise.reject(this.#error(kind, address, stage));
        const found = this.#binding.get(key);
        if (found === undefined) {
            return Promise.reject(this.#error(kind, address, "binding_not_found"));
        }
        return Promise.resolve(found);
    }
    #error(kind, address, stage) {
        return new TenantResolutionError({
            code: "tenant_resolution_failed",
            message: `No VoiceAgent answers ${kind} ${address} (${stage})`,
            subject: { kind: SUBJECT_KIND[kind], address: normalise(kind, address), stage },
        });
    }
}
/** Digits-only for numbers; lowercase otherwise — `channel_binding.address_normalised`. */
function normalise(kind, address) {
    if (kind === "phone_number" || kind === "sip_trunk_number")
        return address.replace(/\D/g, "");
    const at = address.lastIndexOf("@");
    const userPart = address.startsWith("sip:") ? address.slice(4, at < 0 ? undefined : at) : address;
    return userPart.toLowerCase();
}
//# sourceMappingURL=fake-binding-resolver.js.map